import { Decimal } from 'decimal.js';

import moment from 'moment-timezone';

import { ulid } from 'ulid';

import { WsSalesSolutionItem, WsSalesSolutionAction } from '../core';

import { getActionEffort, getCapabilityMaxEffort } from '../core/sales-items';
import { getHoursPerDay } from '../core/sales-timeline';
import { getChildItems, itemIsAction, itemIsCapability } from './item-helpers';
import { WsSalesSolutionOfferingRoleClass } from '../core-sales';
import { findActionById } from '../core/sales-actions';




interface WsSalesTimelineDate {
  date: string;
  actions: WsSalesSolutionAction[];
}




interface WsTimelineAction {
  action: WsSalesSolutionAction;
  effort: number;
  startDate: Date;
  endDate: Date;
}




interface WsTimelineRole {
  action: WsTimelineAction;
  role: WsSalesSolutionOfferingRoleClass;
  effort: number;
  startDate: Date;
  endDate: Date;
}




interface WsPersonRole {
  id: string;
  staticId: string;
  name: string;
  days: { [ key: string]: { effort: Decimal } };
}




interface WsPersonRoleDay {
  day: string;
  effort: Decimal;
}




// console.log('= TEST =');
// const testDays = {
//   '2024-04-01': { effort: zeroDecimal },
//   '2024-04-02': { effort: zeroDecimal },
//   '2024-04-03': { effort: 0 },
//   '2024-04-04': { effort: 0 },
//   '2024-04-05': { effort: 0 },
// };

// addWorkDay(testDays);

// console.log(testDays);




const zeroDecimal = new Decimal(0);

const effortStep = new Decimal(0.1);

const personRoleGap = 7;




const getHoursPerDayDecimal = () => {
  return new Decimal(getHoursPerDay());
};




const isWorkDay = (day: string) => {
  return [ 1, 2, 3, 4, 5 ].includes(moment.tz(day, 'UTC').day());
};




const getDayEffortForRole = (day: string, startDate: Date, endDate: Date, roleValue: number) => {
  // console.log('getDayEffortForRole');

  let effort = new Decimal(0);

  const momentDay = moment.tz(day, 'UTC');

  const momentStart = moment(startDate).tz('UTC');

  const momentEnd = moment(endDate).tz('UTC');


  // console.log(`day   : ${momentDay.clone().startOf('day').toISOString()}`);
  // console.log(`start : ${momentStart.clone().startOf('day').toISOString()}`);
  // console.log(`end   : ${momentEnd.clone().startOf('day').toISOString()}`);


  if (momentDay.clone().startOf('day').toISOString() === momentStart.clone().startOf('day').toISOString()) {
    // console.log('getDayEffortForRole : start');

    const duration = moment.duration(momentStart.diff(momentStart.clone().startOf('day')));

    const durationHours = duration.as('hours');

    const hoursPerDay = getHoursPerDayDecimal();

    effort = effort.add(hoursPerDay.minus(durationHours));


  } else if (momentDay.clone().startOf('day').toISOString() === momentEnd.clone().startOf('day').toISOString()) {
    // console.log('getDayEffortForRole : end');

    const duration = moment.duration(momentEnd.diff(momentEnd.clone().startOf('day')));

    const durationHours = duration.as('hours');

    effort = effort.add(durationHours);


  } else {
    // console.log('getDayEffortForRole : else');

    effort = effort.add(8);
  }
  

  return effort.div(100).mul(roleValue);
};




const findPersonRole = (personRoles: WsPersonRole[], staticId: string, day: string, effortStep: Decimal) => {

  // console.log(`findPersonRole : ${staticId} : ${day} : ${effortStep}`);

  const hoursPerDay = getHoursPerDayDecimal();

  const role = personRoles.find((r) => {

    if (r.staticId === staticId) {
      // console.log('staticId found');

      if (r.days[day]) {
        // console.log(`${r.days[day].effort.toFixed(2)}`);

        const effort = r.days[day].effort.add(effortStep);

        // console.log(`${effort.toFixed(2)} <= ${hoursPerDay.toFixed(2)}`);

        if (effort.lessThanOrEqualTo(hoursPerDay)) {
          return true;

        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  });

  if (role) {
    return role;
  } else {
    return undefined;
  }
};




const getGapLength = (days: { [key: string]: { effort: Decimal } }) => {

  const allDays = Object.keys(days);

  let gapDays = 0;

  const d7 = allDays[allDays.length - 1];
  const d6 = allDays[allDays.length - 2];
  const d5 = allDays[allDays.length - 3];
  const d4 = allDays[allDays.length - 4];
  const d3 = allDays[allDays.length - 5];
  const d2 = allDays[allDays.length - 6];
  const d1 = allDays[allDays.length - 7];


  if (days[d7].effort.equals(0)) { gapDays = gapDays + 1; }
  if (days[d6].effort.equals(0)) { gapDays = gapDays + 1; }
  if (days[d5].effort.equals(0)) { gapDays = gapDays + 1; }
  if (days[d4].effort.equals(0)) { gapDays = gapDays + 1; }
  if (days[d3].effort.equals(0)) { gapDays = gapDays + 1; }
  if (days[d2].effort.equals(0)) { gapDays = gapDays + 1; }
  if (days[d1].effort.equals(0)) { gapDays = gapDays + 1; }

  return gapDays;
};




const addWorkDay = (days: { [key: string]: { effort: Decimal } }) => {
  // console.log('addWorkDay');

  let i = 0;

  while (getGapLength(days) < personRoleGap && i < personRoleGap) {
    // console.log(getGapLength(days));
    // console.log(i);

    const maxDay = Object.keys(days).reduce((prev, current) => {
      return (prev && prev > current) ? prev : current;
    });

    const momentMaxDay = moment.tz(maxDay, 'UTC');

    momentMaxDay.add(1, 'day');

    const addDay = momentMaxDay.format('YYYY-MM-DD');

    days[addDay] = { effort: zeroDecimal };

    i++;
  }
  



  // console.log(`gap days : ${gapDays}`);



  // if (gapDays < personRoleGap) {

  //   const maxDay = Object.keys(days).reduce((prev, current) => {
  //     return (prev && prev > current) ? prev : current;
  //   });

  //   const momentMaxDay = moment.tz(maxDay, 'UTC');

  //   momentMaxDay.add(1, 'day');

  //   const addDay = momentMaxDay.format('YYYY-MM-DD');

  //   days[addDay] = { effort: zeroDecimal };

  //   if ([ 1, 2, 3, 4, 5 ].includes(momentMaxDay.day())) {
  //     // done
  //   } else {
  //     // addWorkDay(days);
  //   }

  //   addWorkDay(days);
  // }
};




const getPersonRoleDays = (startDate: Date, endDate: Date) => {
  const days: { [key: string]: { effort: Decimal } } = {};

  const momentStart = moment(startDate).tz('UTC');

  const momentEnd = moment(endDate).tz('UTC');

  momentEnd.add(personRoleGap, 'days');

  while (momentStart < momentEnd) {

    const day = momentStart.format('YYYY-MM-DD');

    if (!days[day]) {
      days[day] = { effort: zeroDecimal };
    }

    momentStart.add(1, 'day');
  }

  return days;
};




const addEffortToPersonRole = (personRole: WsPersonRole, day: string, effortToAdd: Decimal) => {
  // console.log('addEffortToPersonRole');

  let effort = zeroDecimal;

  if (personRole.days[day]) {
    effort = personRole.days[day].effort;
  }

  const newEffort = (effort).add(effortToAdd);

  personRole.days[day] = { effort: newEffort };
};
















const getTimelineActionsFromAction = (actions: WsSalesSolutionAction[], action: WsSalesSolutionAction, startDate: Date) => {
  const timelineActions: WsTimelineAction[] = [];

    // TODO: itemStart may be derived from:
    //        solution start
    //        adjusted start
    //        dependencies

    const actionOptions: WsSalesActionHelperOptions = {
      startDate,
      actions: actions || [],
      action
    };


    // effort
    const actionEffort = getActionEffort(action, { includeChildActions: true });


    // start/end
    const actionStartDate = getActionStart(actionOptions);
    const actionEndDate = getActionEnd(actionOptions);        


    // log
    if ([ 'Testing Action', 'UAT', 'Parallel Action', 'Final Action' ].includes(action.name)) {
      // console.log('========================================');
      // console.log(action.name);
      // console.log(actionStartDate.toISOString());
      // console.log(actionEndDate.toISOString());
      // console.log(actionDays);
    }


    // action.selected
    if (action.selected) {

      // action
      const timelineAction: WsTimelineAction = {
        action:    action,
        effort:    actionEffort,
        startDate: actionStartDate,
        endDate:   actionEndDate
      };

      timelineActions.push(timelineAction);
    }


    // action.actions

    if (action.actions && Array.isArray(action.actions) && action.actions.length > 0) {
      action.actions.forEach((a) => {
        if (action.actions) {
          const aa = getTimelineActionsFromAction(action.actions, a, actionStartDate);
          timelineActions.push(...aa);
        }
      });
    }

  // return

  return timelineActions;
};





const getTimelineActionsFromCapability = (item: WsSalesSolutionItem, startDate: Date, options: { includeChildCapabilities: boolean }) => {
  const timelineActions: WsTimelineAction[] = [];


  // actions

  if (item.selected) {

    if (item.actions && Array.isArray(item.actions) && item.actions.length > 0) {

      item.actions.forEach((action, index: number) => {

        if (item.actions) {
          const aa = getTimelineActionsFromAction(item.actions, action, startDate);
          timelineActions.push(...aa);
        }

      });
    }
  }


  // items

  if (options.includeChildCapabilities) {
    if (itemIsCapability(item)) {
      if (item.items && Array.isArray(item.items) && item.items.length > 0) {
        item.items.forEach((i) => {
          const ii = getTimelineActionsFromCapability(i, startDate, options);
          timelineActions.push(...ii);
        });
      }
    }
  }


  // return

  return timelineActions;
};






















const getTimelineRoles = (item: WsSalesSolutionItem | WsSalesSolutionAction, startDate: Date, options: { includeChildCapabilities: boolean, items: WsSalesSolutionItem[] }) => {
  const roles: WsTimelineRole[] = [];

  // console.log('====================================================================================================');
  // console.log(`======== ${item.name}`);

  const timelineActions: WsTimelineAction[] = [];

  if (itemIsAction(item)) {

    // console.log('!!! is action !!!');

    const findAction = findActionById(options.items, undefined, item.id, '', { includeChildActions: true, filterSelected: true });

    // console.log(findAction);

    if (findAction && findAction.parent) {
      if (findAction.parent.actions && Array.isArray(findAction.parent.actions)) {
        const ta = getTimelineActionsFromAction(findAction.parent.actions, findAction.action, startDate);
        timelineActions.push(...ta);    
      }
    }

  } else {
    const ta = getTimelineActionsFromCapability(item, startDate, options);
    timelineActions.push(...ta);
  }


  // console.log(timelineActions);


  // each action
  timelineActions.forEach((timelineAction) => {
    // console.log('============================================================');
    // console.log(`==== ${timelineAction.action.name}`);
    // console.log(timelineAction.effort);
    // console.log(timelineAction.startDate.toISOString());
    // console.log(timelineAction.endDate.toISOString());


    // roles
    const actionRoles = getChildItems(timelineAction.action, 'roles', {
      includeChildCapabilities:   false,
      filterSelectedCapabilities: false,
      includeChildActions:        false,
      filterSelectedActions:      true
    }) as [ WsSalesSolutionOfferingRoleClass, WsSalesSolutionItem | WsSalesSolutionAction, string][];


    // each
    actionRoles.forEach((actionRole) => {

      const timelineRole: WsTimelineRole = {
        action: timelineAction,
        role: actionRole[0],
        effort: timelineAction.effort,
        startDate: timelineAction.startDate,
        endDate: timelineAction.endDate
      };

      // console.log('========================================');
      // console.log(timelineRole.role.name);
      // console.log(timelineRole.startDate.toISOString());
      // console.log(timelineRole.endDate.toISOString());

      roles.push(timelineRole);
    });
  });


  // return
  return roles;
};






const getPersonRoleEffort = (personRole: WsPersonRole) => {
  let effort = new Decimal(0);

  const days = getPersonRoleDaysArray(personRole.days);

  days.forEach((dayEffort) => {
    effort = effort.add(dayEffort.effort);
  });

  return effort.toNumber();
};




const getPersonRoleStartDay = (personRole: WsPersonRole) => {

  const days = getPersonRoleDaysArray(personRole.days);

  const firstDay = days[0];

  if (firstDay) {
    return firstDay.day;
  } else {
    return moment().startOf('day').tz('UTC').format('YYYY-MM-DD');
  }
};




const getPersonRoleEndDay = (personRole: WsPersonRole) => {

  const days = getPersonRoleDaysArray(personRole.days);

  if (days.length > 0) {
    const lastDay = days[days.length - 1];

    if (lastDay) {
      return lastDay.day;

    } else {
      return moment().startOf('day').tz('UTC').format('YYYY-MM-DD');
    }
  } else {
    return moment().startOf('day').tz('UTC').format('YYYY-MM-DD');
  }
};









const getPersonRoles = (timelineRoles: WsTimelineRole[]) => {
  const personRoles: WsPersonRole[] = [];

  // const hoursPerDay = getHoursPerDayDecimal();

  // console.log('================================================================================');

  timelineRoles.forEach((timelineRole) => {

    // console.log('========================================');
    // console.log(timelineRole.role.name);
    // console.log(timelineRole.startDate.toISOString());
    // console.log(timelineRole.endDate.toISOString());

    const days = getActionDays(timelineRole.startDate, timelineRole.endDate);

    // let totalEffort = new Decimal(timelineRole.effort);

    // console.log(days);

    days.forEach((day) => {
      // console.log('====================');
      // console.log(day);

      const momentDay = moment.tz(day, 'UTC');
      const weekDay = momentDay.day();

      if ([ 1, 2, 3, 4, 5 ].includes(weekDay)) {

        let dayEffort = getDayEffortForRole(day, timelineRole.startDate, timelineRole.endDate, timelineRole.role.value); // getHoursPerDayDecimal();

        while (dayEffort > zeroDecimal) {

          // console.log(`${day} : ${dayEffort}`);

          let personRole: WsPersonRole | undefined = findPersonRole(personRoles, timelineRole.role.staticId, day, effortStep);

          if (!personRole) {
            // add role

            // console.log('!!!!! ADD ROLE !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!! !!!!!');

            // console.log(`${day}`);

            // console.log(personRoles.length);

            personRole = {
              id:        ulid(),
              staticId:  timelineRole.role.staticId,
              name:      timelineRole.role.name,
              // effort:    new Decimal(timelineRole.effort),
              // startDate: timelineRole.startDate,
              // endDate:   timelineRole.endDate,
              // days:      getPersonRoleDays(timelineRole.startDate, timelineRole.endDate)
              days:      getPersonRoleDays(momentDay.clone().startOf('day').tz('UTC').toDate(), timelineRole.endDate)
            };

            personRoles.push(personRole);
          }

          addEffortToPersonRole(personRole, day, effortStep);


          // add extra week days
          addWorkDay(personRole.days);


          dayEffort = dayEffort.minus(effortStep);
          // totalEffort = totalEffort.minus(effortStep);
        }

      } else {
        // console.log('= SKIP =');
      }
    });
  });

  return personRoles;
};











const getTimelineRolesFromCapabilities = (items: WsSalesSolutionItem[], timelineItems: WsSalesSolutionItem[], startDate: Date) => {
  // const roles: WsPersonRole[] = [];



  timelineItems.forEach((item: WsSalesSolutionItem | WsSalesSolutionAction) => {

    if (item.selected) {

      const timelineRoles = getTimelineRoles(item, startDate, { includeChildCapabilities: false, items });

      const personRoles = getPersonRoles(timelineRoles);

      // console.log('================================================================================');
      // console.log(`== ${item.name}`);
      // console.log('========================================');

      // personRoles.forEach((personRole) => {
      //   console.log('========================================');
      //   console.log(`${personRole.name}`);
      //   Object.keys(personRole.days).forEach((key) => {
      //     const effort = personRole.days[key];
      //     console.log(`${key} : ${effort.effort.toFixed(2)}${isWorkDay(key) ? '' : ' weekend'}`);
      //   });
      // });

      // roles.push(...personRoles);
    }

    // if (itemIsCapability(item)) {
    //   if (item.items && Array.isArray(item.items) && item.items.length > 0) {
    //   }
    // }
  });



  // console.log('');
  // console.log('');
  // console.log('');
  // console.log('================================================================================');

  // roles.forEach((role) => {
  //   console.log('========================================');
  //   console.log(`${role.name}`);

  //   Object.keys(role.days).forEach((key) => {
  //     const effort = role.days[key];
  //     console.log(`${key} : ${effort.effort.toFixed(2)}${isWorkDay(key) ? '' : ' weekend'}`);
  //   });
  // });



  // const maxEffort = getCapabilityMaxEffort(items);

  // const endDate = addEffortToDate(startDate, maxEffort, false);

  // console.log('========================================');
  // console.log(`max effort : ${maxEffort}`);
  // console.log(`end date   : ${endDate.toISOString()}`);

  return [];
};











type WsSalesActionHelperOptions = {
  startDate:   Date;
  actions:     WsSalesSolutionAction[];
  action:      WsSalesSolutionAction;
  // actionIndex: number;
};




const addEffortToDate = (date: Date, effort: number, roundEndOfDay: boolean) => {
  const momentDate = moment(date).tz('UTC');

  // console.log(`utc : ${momentDate.utcOffset()}`);

  const minEffortInHours = new Decimal(0.1);

  const hoursPerDay = getHoursPerDay();

  const zero = new Decimal(0);

  let e = new Decimal(effort);

  while (e > zero) {

    // console.log(`effort : ${e}`);
    // console.log(`hour   : ${momentDate.hour()}`);



    if (momentDate.hour() === hoursPerDay) {

      momentDate.set('hour', 0);

      if (momentDate.day() === 5) {
        momentDate.add(3, 'days');
      } else if (momentDate.day() === 6) {
        momentDate.add(2, 'days');
      } else {
        momentDate.add(1, 'day');
      }
    }



    momentDate.add(6, 'minutes');



    e = e.minus(minEffortInHours);



    if (roundEndOfDay) {

      if (momentDate.hour() === hoursPerDay) {

        momentDate.set('hour', 0);

        if (momentDate.day() === 5) {
          momentDate.add(3, 'days');
        } else if (momentDate.day() === 6) {
          momentDate.add(2, 'days');
        } else {
          momentDate.add(1, 'day');
        }
      }
    }
  }

  return momentDate.toDate();
};




const findActionAndIndex = (actionId: string, actions: WsSalesSolutionAction[]) => {
  let action: WsSalesSolutionAction | undefined = undefined;
  let index: number = -1;

  actions.forEach((a, i) => {
    if (a.id === actionId) {
      action = a;
      index  = i;
    }
  });

  return {
    action,
    index
  };
};




const getActionStart = (options: WsSalesActionHelperOptions): Date => {

  const actionAndIndex = findActionAndIndex(options.action.id, options.actions);



  const log = [ 'false' ].includes(options.action.name) ? true : false;
  log && console.log('----------------------------------------');
  log && console.log(options.action.name);



  let totalEffortBeforeStart: number = 0;

  if (actionAndIndex.action && actionAndIndex.index > -1) {

    options.actions.forEach((action, index: number) => {

      if (action.selected) {

        log && console.log('----------');
        log && console.log(action.name);

        if (index < actionAndIndex.index) {

          log && console.log('add before start');

          const actionEffort = getActionEffort(action, { includeChildActions: true });

          totalEffortBeforeStart = totalEffortBeforeStart + actionEffort;

          log && console.log(`effort : ${actionEffort}`);
        }

        log && console.log(`total : ${totalEffortBeforeStart}`);
        log && console.log('----------');
      }
    });
  }

  log && console.log('----------------------------------------');

  const date = addEffortToDate(options.startDate, totalEffortBeforeStart, true);

  return date;
};




const getActionEnd = (options: WsSalesActionHelperOptions): Date => {

  const actionStart = getActionStart(options);

  const actionEffort = getActionEffort(options.action, { includeChildActions: true });

  const date = addEffortToDate(actionStart, actionEffort, false);

  return date;
};




const getActionDays = (actionStartDate: Date, actionEndDate: Date) => {
  const days: string[] = [];

  const momentDate = moment(actionStartDate).tz('UTC');

  while (momentDate.clone().startOf('day').toDate() < actionEndDate) {
    const day = momentDate.format('YYYY-MM-DD');
    days.push(day);
    momentDate.add(1, 'day');
  }

  return days;
};




const getUniqueRoles = (list: any[]) => {
  if (process.env.NODE_ENV === 'production') {

    const roles: { [ key: string ]: any } = {};

    list.forEach((listItemAndActionItem, index: number) => {
      const listItem   = listItemAndActionItem[0];
      const listAction = listItemAndActionItem[1];
      const listIndex  = listItemAndActionItem[2];

      if (roles[listItem.name]) {
      } else {
        roles[listItem.name] = listItemAndActionItem
      }
    });

    return Object.values(roles);

  } else {
    return list;
  }
};











function shouldRemove(day: { effort: Decimal }) {
  return day.effort.equals(0);
}

const getPersonRoleDaysArray = (days: { [key: string]: { effort: Decimal } }) => {

  const array: WsPersonRoleDay[] = Object.keys(days).map((day) => {
    const d: WsPersonRoleDay = {
      day: day,
      effort: days[day].effort
    };

    return d;
  });

  // remove
  while (array.length > 0 && shouldRemove(array[array.length - 1])) {
    array.pop();
  }

  // return
  return array;
};




export type {
  WsPersonRole,
  WsSalesTimelineDate
};

export {
  isWorkDay,
  getPersonRoleDaysArray,
  getTimelineRolesFromCapabilities,
  getActionStart,
  getActionEnd,
  getTimelineRoles,
  getPersonRoles,
  getUniqueRoles,

  getPersonRoleEffort,
  getPersonRoleStartDay,
  getPersonRoleEndDay
};
